import { createContext, type FormEvent, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon.js';

// Modal & konfirmasi — struktur HTML sama dengan modal()/confirmBox() v1 (core.js):
// #modalRoot > .modal-back > .modal[.wide] > form[novalidate] > .modal-h + .modal-b + .modal-f(.form-error + foot)

export interface ModalHandle {
  close: () => void;
  /** Tampilkan pesan error di kaki modal (kosong = hapus). */
  error: (msg?: string) => void;
  /** Jalankan aksi: error otomatis tampil di modal, tombol dinonaktifkan selama berjalan. */
  run: (fn: () => Promise<void> | void, btn?: HTMLButtonElement | null) => Promise<void>;
}

export interface ModalOptions {
  title: ReactNode;
  /** Isi modal; fungsi menerima handle (mis. untuk menutup dari dalam). */
  body: ReactNode | ((m: ModalHandle) => ReactNode);
  /** Isi kaki modal; false = tanpa kaki. */
  foot?: ReactNode | ((m: ModalHandle) => ReactNode) | false;
  wide?: boolean;
  /** Tombol tutup (×) disembunyikan (mis. wajib ganti password). */
  noClose?: boolean;
  submit?: (form: HTMLFormElement, m: ModalHandle) => Promise<void> | void;
  onClose?: () => void;
}

interface Entry extends ModalOptions {
  id: number;
}

const Ctx = createContext<{ open: (o: ModalOptions) => ModalHandle } | null>(null);

export function useModal() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useModal di luar <ModalProvider>');
  return c;
}

function ModalView({ entry, onClose }: { entry: Entry; onClose: (id: number) => void }) {
  const [err, setErr] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const handle = useMemo<ModalHandle>(() => {
    const h: ModalHandle = {
      close: () => onClose(entry.id),
      error: (msg) => setErr(msg || ''),
      run: async (fn, btn) => {
        setErr('');
        if (btn) btn.disabled = true;
        try {
          await fn();
        } catch (e) {
          setErr((e as Error).message);
        } finally {
          if (btn?.isConnected) btn.disabled = false;
        }
      },
    };
    return h;
  }, [entry.id, onClose]);

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!entry.submit) return;
    const form = e.currentTarget;
    const btn = form.querySelector<HTMLButtonElement>('button[type=submit]');
    void handle.run(() => entry.submit?.(form, handle), btn);
  };

  const body = typeof entry.body === 'function' ? entry.body(handle) : entry.body;
  const foot = typeof entry.foot === 'function' ? entry.foot(handle) : entry.foot;
  return (
    <div className="modal-back">
      <div className={`modal ${entry.wide ? 'wide' : ''}`} role="dialog" aria-modal="true">
        <form noValidate ref={formRef} onSubmit={submit}>
          <div className="modal-h">
            <h3>{entry.title}</h3>
            <button
              type="button"
              className={`icon-btn${entry.noClose ? ' hidden' : ''}`}
              data-m="close"
              aria-label="Tutup"
              onClick={handle.close}
            >
              <Icon n="x" />
            </button>
          </div>
          <div className="modal-b">{body}</div>
          {entry.foot === false ? null : (
            <div className="modal-f">
              <div className="form-error" role="alert">
                {err}
              </div>
              {foot}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export function ModalProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<Entry[]>([]);
  const seq = useRef(0);
  const close = useCallback((id: number) => {
    setStack((s) => {
      const e = s.find((x) => x.id === id);
      e?.onClose?.();
      return s.filter((x) => x.id !== id);
    });
  }, []);
  const api = useMemo(
    () => ({
      open: (o: ModalOptions): ModalHandle => {
        seq.current += 1;
        const id = seq.current;
        setStack((s) => [...s, { ...o, id }]);
        return { close: () => close(id), error: () => {}, run: async (fn) => void (await fn()) };
      },
    }),
    [close],
  );
  const root = document.getElementById('modalRoot');
  return (
    <Ctx.Provider value={api}>
      {children}
      {root
        ? createPortal(
            stack.map((e) => <ModalView key={e.id} entry={e} onClose={close} />),
            root,
          )
        : null}
    </Ctx.Provider>
  );
}

/** Kotak konfirmasi (v1 confirmBox). input = label isian wajib → mengembalikan teksnya; batal → null. */
export function useConfirm() {
  const { open } = useModal();
  return useCallback(
    (title: string, text: ReactNode, { ok = 'Ya, lanjutkan', danger = false, input = null as string | null } = {}) =>
      new Promise<string | true | null>((resolve) => {
        let done = false;
        const m = open({
          title,
          body: (
            <>
              <p>{text}</p>
              {input ? (
                <label className="f">
                  {input}
                  <textarea autoComplete="off" className="inp" name="reason" required />
                </label>
              ) : null}
            </>
          ),
          foot: (
            <>
              <button type="button" className="btn" data-m="close" onClick={() => m.close()}>
                Batal
              </button>
              <button type="submit" className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}>
                {ok}
              </button>
            </>
          ),
          submit: (form) => {
            const v = input ? (form.elements.namedItem('reason') as HTMLTextAreaElement).value.trim() : true;
            if (input && !v) throw new Error(`${input} wajib diisi.`);
            done = true;
            m.close();
            resolve(v);
          },
          onClose: () => {
            if (!done) resolve(null);
          },
        });
      }),
    [open],
  );
}
